import { FunctionComponent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import sprite from '../../../../assets/icons/sprite.svg';
import { Switch } from '../../../../shared';

import classNames from 'classnames';
import classes from './ChannelPreferences.module.scss';

interface NotificationStory {
  id: number;
  title: string;
  subscriptionStatus: boolean;
}

export interface NotificationStoryChange {
  storyId: number;
  isEnabled: boolean;
}

export interface ChannelNotificationChange {
  channelId: number;
  isEnabled: boolean;
}

interface ChannelPreferencesProps {
  channelId: number;
  name: string;
  stories: NotificationStory[];
  enabledStoriesCount: number;
  totalStoriesCount: number;
  isChannelEnabled: boolean;
  isStoryEnabled: (story: NotificationStory) => boolean;
  isOpen: boolean;
  onOpenToggle: () => void;
  onChannelChange: (args: ChannelNotificationChange) => void;
  onStoryChange: (args: NotificationStoryChange) => void;
}

export const ChannelPreferences: FunctionComponent<ChannelPreferencesProps> = ({
  channelId,
  name,
  stories,
  enabledStoriesCount,
  totalStoriesCount,
  isChannelEnabled,
  isStoryEnabled,
  isOpen,
  onOpenToggle,
  onChannelChange,
  onStoryChange,
}) => {
  const { t: ctxT } = useTranslation('', { keyPrefix: 'pushNotifications' });

  const isExpandable = stories.length > 0;

  const body = useMemo(() => {
    if (!isOpen || !isExpandable) {
      return null;
    }

    return (
      <div className={classes['channel-preferences__body']}>
        {stories.map((story) => {
          const { id: storyId, title } = story;

          return (
            <Switch
              key={storyId}
              label={title}
              className={classNames(classes['channel-preferences__story'], {
                [classes['channel-preferences__story--disabled']]: !isChannelEnabled,
              })}
              checked={isStoryEnabled(story)}
              disabled={!isChannelEnabled}
              onChange={(isEnabled) => onStoryChange({ storyId, isEnabled })}
            />
          );
        })}
      </div>
    );
  }, [isChannelEnabled, isExpandable, isOpen, isStoryEnabled, onStoryChange, stories]);

  const trigger = useMemo(() => {
    const chevron = (
      <svg
        className={classNames(classes['channel-preferences__chevron'], {
          [classes['channel-preferences__chevron--open']]: isOpen,
          [classes['channel-preferences__chevron--hidden']]: !isExpandable,
        })}
      >
        <use href={`${sprite}#down-arrow`} />
      </svg>
    );

    const summary = (
      <>
        <span className={classes['channel-preferences__name']}>{name}</span>
        <span className={classes['channel-preferences__count']}>
          {ctxT(`channel-${isChannelEnabled ? 'stories-count' : 'off'}`, {
            enabled: enabledStoriesCount,
            total: totalStoriesCount,
          })}
        </span>
      </>
    );

    if (!isExpandable) {
      return (
        <div
          className={classNames(
            classes['channel-preferences__trigger'],
            classes['channel-preferences__trigger--static']
          )}
        >
          {chevron}
          {summary}
        </div>
      );
    }

    return (
      <button
        type={'button'}
        className={classes['channel-preferences__trigger']}
        aria-expanded={isOpen}
        onClick={onOpenToggle}
      >
        {chevron}
        {summary}
      </button>
    );
  }, [
    ctxT,
    enabledStoriesCount,
    isChannelEnabled,
    isExpandable,
    isOpen,
    name,
    onOpenToggle,
    totalStoriesCount,
  ]);

  return (
    <div className={classes['channel-preferences']}>
      <div className={classes['channel-preferences__head']}>
        {trigger}
        <Switch
          className={classes['channel-preferences__switch']}
          ariaLabel={ctxT('channel-switch-label', { name })}
          checked={isChannelEnabled}
          onChange={(isEnabled) => onChannelChange({ channelId, isEnabled })}
        />
      </div>
      {body}
    </div>
  );
};
